import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

import type { RepeatableGroupConfiguration, RepeatableSubField } from '../../../interfaces/input.interface';
import { InputType } from '../../../interfaces/input.interface';
import type { PendingDropdownItem, RepeatableHydrationResult, RepeatableLogger } from './repeatable-group.types';
import { RepeatableDropdownResolver } from './repeatable-dropdown.resolver';
import { RepeatableRowFormFactory } from './repeatable-row-form.factory';
import { RepeatableValueUtils } from './repeatable-value.utils';

export type RepeatableHydrateOptions = {
    model: FormGroup | null;
    rowsArray: FormArray<FormGroup>;
    configuration: RepeatableGroupConfiguration;
    isEditable: boolean;
    maxRows: number;
};

export class RepeatableModelHydrator {
    public constructor(
        private readonly dropdownResolver: RepeatableDropdownResolver,
        private readonly logger: RepeatableLogger,
    ) {}

    public hydrateFromModel(options: RepeatableHydrateOptions): RepeatableHydrationResult {
        if (!options.model) {
            this.logger.warn('hydrateFromModel(): model missing.');
            return { pendingDropdown: [] };
        }

        const entries = Object.entries(options.configuration.bindings ?? {});

        if (entries.length === 0) {
            this.logger.warn('hydrateFromModel(): No bindings found.');
            return { pendingDropdown: [] };
        }

        const separator = options.configuration.serialization?.separator ?? ';';
        const splitByControl: Record<string, string[]> = {};
        let maxLength = 0;

        for (const [logicalKey, targetControlName] of entries) {
            const rawValue = options.model.get(targetControlName)?.value;
            const parts = RepeatableValueUtils.splitSerializedKeepEmptiesAndSanitize(rawValue, separator);

            splitByControl[logicalKey] = parts;
            maxLength = Math.max(maxLength, parts.length);

            this.logger.log('hydrate split', {
                logicalKey,
                targetControlName,
                rawValue,
                parts,
                partsLength: parts.length,
            });
        }

        const desiredRows = Math.min(Math.max(maxLength, 1), options.maxRows);
        const subFields = options.configuration.subFields ?? [];
        const pendingDropdown: PendingDropdownItem[] = [];

        RepeatableRowFormFactory.ensureRowsCount(options.rowsArray, desiredRows, subFields);

        for (const [logicalKey] of entries) {
            const subField = subFields.find((item) => item.key === logicalKey);

            if (!subField) {
                this.logger.warn('hydrateFromModel(): subField not found for logicalKey.', { logicalKey });
                continue;
            }

            const values = splitByControl[logicalKey] ?? [];

            for (let rowIndex = 0; rowIndex < options.rowsArray.length; rowIndex++) {
                const row = options.rowsArray.at(rowIndex);
                const control = row.get(logicalKey);

                if (!control) {
                    this.logger.warn('hydrate: missing row control.', { rowIndex, logicalKey });
                    continue;
                }

                const token = String(values[rowIndex] ?? '').trim();

                if (token === '' || RepeatableValueUtils.isNullishStringToken(token)) {
                    control.patchValue(null, { emitEvent: false });
                    continue;
                }

                this.hydrateCell({
                    control,
                    subField,
                    rowIndex,
                    logicalKey,
                    token,
                    isEditable: options.isEditable,
                    pendingDropdown,
                });
            }
        }

        return { pendingDropdown };
    }

    private hydrateCell(options: {
        control: AbstractControl;
        subField: RepeatableSubField;
        rowIndex: number;
        logicalKey: string;
        token: string;
        isEditable: boolean;
        pendingDropdown: PendingDropdownItem[];
    }): void {
        const type = options.subField.input?.type;
        const isSelect = RepeatableValueUtils.isSelectType(type);

        if (!options.isEditable && isSelect && !RepeatableValueUtils.isNumericLike(options.token)) {
            options.control.patchValue(options.token, { emitEvent: false });
            return;
        }

        if (type === InputType.NUMERIC) {
            const parsedValue = RepeatableValueUtils.parseFlexibleNumber(options.token);

            if (parsedValue === null) {
                this.logger.warn('NUMERIC token not parseable -> input type=number will show blank.', {
                    key: options.logicalKey,
                    token: options.token,
                });
            }

            options.control.patchValue(parsedValue, { emitEvent: false });
            return;
        }

        if (isSelect) {
            this.hydrateSelectCell(options);
            return;
        }

        options.control.patchValue(options.token, { emitEvent: false });
    }

    private hydrateSelectCell(options: {
        control: AbstractControl;
        subField: RepeatableSubField;
        rowIndex: number;
        logicalKey: string;
        token: string;
        pendingDropdown: PendingDropdownItem[];
    }): void {
        const type = options.subField.input?.type;
        const primaryKey = options.subField.input?.dropdownConfiguration?.controlValueKey
            ?? options.subField.input?.dropdownConfiguration?.valuePrimaryKey
            ?? 'id';
        const isMulti = RepeatableValueUtils.isMultiSelectType(type);

        if (!isMulti) {
            const selectedValue = this.dropdownResolver.mapIdToDropdownValue(options.token, options.subField, options.rowIndex);

            if (selectedValue !== null && selectedValue !== undefined) {
                options.control.patchValue(selectedValue, { emitEvent: false });
                return;
            }

            options.control.patchValue(null, { emitEvent: false });
            this.dropdownResolver.logWhySelectCannotRender(options.subField, options.rowIndex, options.token);
            this.logger.warn('hydrateSelectCell(): unresolved select token.', {
                rowIndex: options.rowIndex,
                logicalKey: options.logicalKey,
                token: options.token,
                key: options.subField.key,
                optionsLength: this.dropdownResolver.options(options.subField, options.rowIndex).length,
            });

            options.pendingDropdown.push({
                rowIndex: options.rowIndex,
                logicalKey: options.logicalKey,
                rawIds: [options.token],
                sf: options.subField,
                isMulti: false,
            });
            return;
        }

        const ids = RepeatableValueUtils.parseIdsFromToken(options.token, ',', primaryKey);
        const dropdownOptions = this.dropdownResolver.options(options.subField, options.rowIndex);
        const resolvedValues = ids
            .map((id) => dropdownOptions.find((option) => RepeatableValueUtils.getOptionKey(option?.value, primaryKey) === id)?.value ?? null)
            .filter((value) => value !== null && value !== undefined);

        options.control.patchValue(resolvedValues, { emitEvent: false });

        const unresolvedIds = ids.filter((id) => !dropdownOptions.some((option) => RepeatableValueUtils.getOptionKey(option?.value, primaryKey) === id));

        if (unresolvedIds.length > 0) {
            options.pendingDropdown.push({
                rowIndex: options.rowIndex,
                logicalKey: options.logicalKey,
                rawIds: unresolvedIds,
                sf: options.subField,
                isMulti: true,
            });
        }
    }
}
